import { motion } from "framer-motion";
import { Check, X } from "lucide-react";

const ROWS = [
  { feature: "Creator sourcing", prulo: "2,000+ internal roster with direct rates & contacts", legacy: "Rented lists, outbound at scale" },
  { feature: "Pricing", prulo: "Fair, transparent, no hidden markup", legacy: "Inflated retainers, opaque line items" },
  { feature: "Reporting", prulo: "Live dashboards, real numbers", legacy: "PDF decks, weeks after the fact" },
  { feature: "Owned distribution", prulo: "Fan pages, lyric pages, auto-run seeding", legacy: "None, everything is rented" },
  { feature: "Turnaround", prulo: "24–48 hours, brief to live", legacy: "3–6 weeks to campaign go-live" },
  { feature: "Chart placement", prulo: "Billboard, UK, Viral 50, proven", legacy: "Vanity impressions, no chart accountability" },
];

export default function WhyPrulo() {
  return (
    <section data-testid="home-why-prulo" className="relative py-10 md:py-14 border-t border-white/5">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-zinc-500 mb-4">Why Prulo</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-2xl">
              Built different<br /><span className="italic font-light">from every agency before us.</span>
            </h2>
          </div>
          <p className="max-w-sm text-zinc-400">
            Legacy agencies rent creator lists and mark up the invoice.
            We built the roster, own the distribution, and pass the savings to the artist.
          </p>
        </div>

        {/* Desktop / tablet: 3-column table */}
        <div className="hidden md:block rounded-3xl border border-white/10 overflow-hidden">
          <div className="grid grid-cols-3 border-b border-white/10 bg-white/[0.02]">
            <div className="p-5 text-xs uppercase tracking-[0.2em] text-zinc-500">Capability</div>
            <div className="p-5 text-xs uppercase tracking-[0.2em] text-white font-medium border-l border-white/10">Prulo</div>
            <div className="p-5 text-xs uppercase tracking-[0.2em] text-zinc-500 border-l border-white/10">Legacy agency</div>
          </div>
          {ROWS.map((r, i) => (
            <motion.div
              key={r.feature}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className={`grid grid-cols-3 ${i !== ROWS.length - 1 ? "border-b border-white/10" : ""}`}
              data-testid={`why-prulo-row-${i}`}
            >
              <div className="p-5 text-sm text-zinc-300">{r.feature}</div>
              <div className="p-5 text-sm text-white flex items-start gap-3 border-l border-white/10">
                <Check size={16} className="mt-0.5 flex-none text-white" />
                <span>{r.prulo}</span>
              </div>
              <div className="p-5 text-sm text-zinc-500 flex items-start gap-3 border-l border-white/10">
                <X size={16} className="mt-0.5 flex-none text-zinc-600" />
                <span>{r.legacy}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile: stacked cards per feature */}
        <div className="md:hidden space-y-4">
          {ROWS.map((r, i) => (
            <motion.div
              key={r.feature}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              className="rounded-2xl border border-white/10 overflow-hidden"
              data-testid={`why-prulo-row-mobile-${i}`}
            >
              <div className="px-5 py-3 text-xs uppercase tracking-[0.2em] text-zinc-400 bg-white/[0.03] border-b border-white/10">
                {r.feature}
              </div>
              <div className="px-5 py-4 flex items-start gap-3 border-b border-white/5">
                <Check size={16} className="mt-1 flex-none text-white" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-1">Prulo</div>
                  <div className="text-sm text-white leading-relaxed">{r.prulo}</div>
                </div>
              </div>
              <div className="px-5 py-4 flex items-start gap-3">
                <X size={16} className="mt-1 flex-none text-zinc-600" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-1">Legacy agency</div>
                  <div className="text-sm text-zinc-500 leading-relaxed">{r.legacy}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
